import { ReactElement, SyntheticEvent } from 'react';
import { Box, Tab, Tabs, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import InsightsIcon from '@mui/icons-material/Insights';
import BarChartIcon from '@mui/icons-material/BarChart';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AssessmentIcon from '@mui/icons-material/Assessment';
import GroupWorkIcon from '@mui/icons-material/GroupWork';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';

export type DashboardTabKey =
  | 'sales'
  | 'trends'
  | 'top'
  | 'branches'
  | 'tables'
  | 'demographics'
  | 'segmentation';

const TAB_META: Record<
  DashboardTabKey,
  { labelKey: string; icon: ReactElement }
> = {
  sales: { labelKey: 'salesReport.tab', icon: <AssessmentIcon /> },
  trends: { labelKey: 'dashboard.sales.trends', icon: <InsightsIcon /> },
  top: { labelKey: 'dashboard.top.selling.products', icon: <BarChartIcon /> },
  branches: {
    labelKey: 'dashboard.branch.performance',
    icon: <StorefrontIcon />
  },
  tables: { labelKey: 'tableReports.tab', icon: <TableRestaurantIcon /> },
  demographics: {
    labelKey: 'dashboard.customer.demographics',
    icon: <PeopleAltIcon />
  },
  segmentation: {
    labelKey: 'dashboard.customer.segmentation',
    icon: <GroupWorkIcon />
  }
};

interface DashboardTabsProps {
  /** Tabs to show, in order. */
  tabs: DashboardTabKey[];
  activeTab: DashboardTabKey;
  onTabChange: (event: SyntheticEvent, tab: DashboardTabKey) => void;
}

const TabsContainerWrapper = styled(Box)(
  ({ theme }) => `
      padding: 0 ${theme.spacing(2)};
      position: relative;
      margin-bottom: ${theme.spacing(2)};

      .MuiTabs-scrollableX {
        overflow-x: auto !important;
      }
  `
);

const DashboardTabs = ({
  tabs,
  activeTab,
  onTabChange
}: DashboardTabsProps) => {
  const { t } = useTranslation();

  return (
    <TabsContainerWrapper className="dashboard-tabs">
      <Tabs
        value={activeTab}
        onChange={onTabChange}
        variant="scrollable"
        scrollButtons="auto"
        textColor="primary"
        indicatorColor="primary"
        aria-label="dashboard tabs"
      >
        {tabs.map((tab) => (
          <Tab
            key={tab}
            value={tab}
            icon={TAB_META[tab].icon}
            iconPosition="start"
            label={t(TAB_META[tab].labelKey)}
            id={`dashboard-tab-${tab}`}
            aria-controls={`dashboard-tabpanel-${tab}`}
          />
        ))}
      </Tabs>
    </TabsContainerWrapper>
  );
};

export default DashboardTabs;
